import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import type { DataToGetDocumentsFromWeb } from "../models/DataToGetDocumentsFromWeb";
import type { DocumentParts } from "../models/DocumentParts";

const DOCS_DB = import.meta.env.VITE_DOCS_DB || "docs_cmms";

export const getDocumentsWeb = async (
	dataToGetDocuments: DataToGetDocumentsFromWeb,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	if (dataToGetDocuments.TreeId !== 0) {
		parameters.push({
			name: "TreeId",
			value: Number.parseInt(dataToGetDocuments.TreeId as string),
		});
	} else {
		parameters.push({ name: "TreeId", value: null });
	}

	parameters.push({ name: "Search", value: dataToGetDocuments.Search });
	parameters.push({
		name: "IncludeHistory",
		value: dataToGetDocuments.IncludeHistory,
	});
	parameters.push({
		name: "IncludeObsolete",
		value: dataToGetDocuments.IsObsolete,
	});

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[GetDocumentsWeb]`,
		parameters,
	);
	return resp;
};

export const createNewDocument = async (
	TreeId: number | string | null,
	Title: string,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "TreeId",
		value: TreeId,
	});

	parameters.push({
		name: "Title",
		value: Title,
	});

	try {
		const resp = await apiService.call(
			`[${DOCS_DB}].[DOCS].[CreateDocument]`,
			parameters,
		);
		return {
			ok: true,
			data: resp,
		};
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};

export const updateMetaData = async (
	DocumentId: number | null,
	Title: string,
	VendorName: string,
	VendorDatetime: Date | string | null,
	VendorVersion: string,
	VendorDocument: string,
	DocumentNumber: string | null,
	TreeId: number | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "DocumentId",
		value: DocumentId,
	});

	parameters.push({
		name: "Title",
		value: Title,
	});
	parameters.push({
		name: "VendorName",
		value: VendorName,
	});
	parameters.push({
		name: "VendorDatetime",
		value: VendorDatetime,
	});
	parameters.push({
		name: "VendorVersion",
		value: VendorVersion,
	});
	parameters.push({
		name: "VendorDocument",
		value: VendorDocument,
	});
	parameters.push({
		name: "DocumentNumber",
		value: DocumentNumber,
	});
	parameters.push({
		name: "TreeId",
		value: TreeId,
	});

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[UpdateDocumentMetadata]`,
		parameters,
	);
	return resp;
};

export const createNewVersion = async (
	DocumentId: number | null,
	UploadedFullPath: string | null,
	FileSize: number | null,
	FileTimestamp: string | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "DocumentId",
		value: DocumentId,
	});

	parameters.push({
		name: "UploadedFullPath",
		value: UploadedFullPath,
	});
	parameters.push({
		name: "FileTimestamp",
		value: FileTimestamp,
	});
	parameters.push({
		name: "FileSize",
		value: FileSize,
	});

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[CreateVersion]`,
		parameters,
	);
	return resp;
};

export const getCurrentVersion = async (DocumentId: number | null) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "DocumentId",
		value: DocumentId,
	});

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[GetCurrentVersion]`,
		parameters,
	);
	return resp;
};

export const getVersions = async (DocumentId: number | null) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "DocumentId",
		value: DocumentId,
	});

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[GetVersions]`,
		parameters,
	);
	return resp;
};

export const deleteDocument = async (DocumentId: number | null) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "DocumentId",
		value: DocumentId,
	});

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[DeleteDocument]`,
		parameters,
	);
	return resp;
};

export const deleteVersion = async (VersionId: number | null) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "VersionId",
		value: VersionId,
	});

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[DeleteVersion]`,
		parameters,
	);
	return resp;
};

export const flagDocument = async (
	DocumentId: number | null,
	FlagValue: boolean,
	UpdatingComment: string | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "DocumentId",
		value: DocumentId,
	});
	parameters.push({
		name: "FlagValue",
		value: FlagValue,
	});
	parameters.push({
		name: "UpdatingComment",
		value: UpdatingComment,
	});

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[FlagDocument]`,
		parameters,
	);
	return resp;
};

export const updateDocumentIsObsolete = async (
	DocumentId: number | null,
	IsObsolete: boolean,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "DocumentId",
		value: DocumentId,
	});
	parameters.push({
		name: "IsObsolete",
		value: IsObsolete,
	});

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[UpdateDocumentIsObsolete]`,
		parameters,
	);
	return resp;
};

export const getTree = async () => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2(`[${DOCS_DB}].[DOCS].[GetTree]`, []);
	return resp;
};

export const getAssetExtensionData = async (
	AssetId: string | number | undefined,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});

	const resp = await apiService.callV2(
		"[MMS].[GetAssetExtensionData]",
		parameters,
	);
	return resp;
};

export const getPartsDataFromWarehouse = async (DocumentId: number | null) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "DocumentId", value: DocumentId });

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[GetPartsDataFromWarehouse]`,
		parameters,
	);
	return resp;
};

export const getParts = async () => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[GetParts]`,
		parameters,
	);
	return resp;
};

export const getDocumentsPartsByDocumentId = async (
	DocumentId: number | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "DocumentId", value: DocumentId });

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[GetDocumentsPartsByDocumentId]`,
		parameters,
	);
	return resp;
};

export const upsertDocumentParts = async (
	DocumentId: number | null,
	Parts: DocumentParts[],
) => {
	const apiService = new MESApiService();

	const partsIds = Parts.map((item) => item.PartId);
	const partIdsString = partsIds.join(",");

	const parameters: spParameter[] = [];

	parameters.push({ name: "DocumentId", value: DocumentId });
	parameters.push({ name: "PartIds", value: partIdsString });

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[UpsertDocumentParts]`,
		parameters,
	);
	return resp;
};

export const getAssetDrawings = async (DocumentName: string | null) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "DocumentName", value: DocumentName as string });

	const resp = await apiService.callV2("[MMS].[GetAssetDrawings]", parameters);
	return resp;
};

export const addLinkAssetDrawing = async (
	AssetIdsToAdd: any,
	DocumentName: string,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "AssetIdsToAdd", value: AssetIdsToAdd.join(",") });
	parameters.push({ name: "DocumentName", value: DocumentName });

	const resp = await apiService.callV2(
		"[MMS].[UpsertAssetDrawings]",
		parameters,
	);
	return resp;
};

export const updatePDFVersion = async (
	VersionId: number | null,
	PDFPath: string,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	parameters.push({ name: "VersionId", value: VersionId });
	parameters.push({ name: "PDFPath", value: PDFPath });

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[UpdatePDFVersion]`,
		parameters,
	);
	return resp;
};
